import React, { useState, useEffect } from 'react';
import StripeConnectService from '../services/StripeConnectService';

export default function StripeConnect() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [error, setError] = useState(null);
    const [connectingLoading, setConnectingLoading] = useState(false);

    useEffect(() => {
        fetchStatus();

        // Check for return from Stripe
        const urlParams = new URLSearchParams(window.location.search);
        const stripeConnect = urlParams.get('stripe_connect');

        if (stripeConnect === 'success') {
            // Refresh status after successful connection
            setTimeout(() => {
                fetchStatus();
            }, 1000);
        } else if (stripeConnect === 'refresh') {
            setError('Onboarding was interrupted. Please try again.');
        }
    }, []);

    const fetchStatus = () => {
        setLoading(true);
        StripeConnectService.getAccountStatus()
            .then(response => {
                setStatus(response.data);
                setLoading(false);
            })
            .catch(e => {
                console.error("Failed to fetch Stripe Connect status:", e);
                setError("Failed to load Stripe Connect status.");
                setLoading(false);
            });
    };

    const handleConnect = (e) => {
        e.preventDefault();

        if (!email) {
            setError('Please enter an email address.');
            return;
        }

        setConnectingLoading(true);
        setError(null);

        StripeConnectService.createAccountLink(email)
            .then(response => {
                // Redirect to Stripe onboarding
                window.location.href = response.data.url;
            })
            .catch(e => {
                console.error("Failed to create Stripe Connect link:", e);
                setError(e.response?.data?.message || "Failed to start Stripe Connect onboarding.");
                setConnectingLoading(false);
            });
    };

    const handleDisconnect = () => {
        if (!confirm('Are you sure you want to disconnect your Stripe account? This will prevent you from receiving payments.')) {
            return;
        }

        setLoading(true);
        StripeConnectService.disconnect()
            .then(() => {
                fetchStatus();
            })
            .catch(e => {
                console.error("Failed to disconnect Stripe account:", e);
                setError("Failed to disconnect Stripe account.");
                setLoading(false);
            });
    };

    if (loading) {
        return (
            <div className="bg-white shadow sm:rounded-lg p-6">
                <p className="text-gray-500">Loading Stripe Connect status...</p>
            </div>
        );
    }

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Stripe Connect Integration
                </h3>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                    <p>
                        Connect your Stripe account to receive payments from ticket sales.
                        A service fee (% + fixed fee) per transaction will be deducted.
                    </p>
                </div>

                {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

                {!status?.connected ? (
                    <form onSubmit={handleConnect} className="mt-5">
                        <div className="mb-4">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email Address
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                placeholder="your@email.com"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                This email will be used for your Stripe account.
                            </p>
                        </div>
                        <button
                            type="submit"
                            disabled={connectingLoading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                            {connectingLoading ? 'Connecting...' : 'Connect Stripe Account'}
                        </button>
                    </form>
                ) : (
                    <div className="mt-5">
                        <div className="rounded-md bg-green-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-green-800">
                                        Stripe Account Connected
                                    </h3>
                                    <div className="mt-2 text-sm text-green-700">
                                        <p>Account ID: <code className="bg-green-100 px-1 rounded">{status.account_id}</code></p>
                                        <p className="mt-1">Charges enabled: {status.charges_enabled ? 'Yes' : 'No'}</p>
                                        <p className="mt-1">Payouts enabled: {status.payouts_enabled ? 'Yes' : 'No'}</p>
                                        <p className="mt-1">Details submitted: {status.details_submitted ? 'Yes' : 'No'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!status.charges_enabled && status.requirements && status.requirements.length > 0 && (
                            <div className="mt-4 rounded-md bg-yellow-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-yellow-800">
                                            Additional Information Required
                                        </h3>
                                        <div className="mt-2 text-sm text-yellow-700">
                                            <p>Your Stripe account requires additional information before charges can be enabled.</p>
                                            <button
                                                onClick={handleConnect}
                                                className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                                            >
                                                Complete onboarding
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-5">
                            <button
                                type="button"
                                onClick={handleDisconnect}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Disconnect Stripe Account
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
